import type { CountryCodeAlpha3, Timezone } from 'common/api/api-types';
import type { SelectedDateRange } from 'common/components/DateRangeSelect';

import { type TZDate, tz } from '@date-fns/tz';
import {
  addMinutes,
  type Day,
  differenceInDays,
  eachMinuteOfInterval,
  endOfDay,
  format,
  formatRelative,
  getHours,
  isAfter,
  isDate,
  isFuture,
  isToday,
  isValid,
  type Locale,
  parse,
  startOfDay,
  subDays,
} from 'date-fns';
import { enGB as localeEnGB } from 'date-fns/locale/en-GB';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import { MONDAY, SUNDAY } from 'common/constants';

export const addLate = (date?: Date) => (date && isFuture(date) ? '' : ' late');

export const formatDate = (date?: Date, defaultOutput?: string) =>
  isDate(date) ? format(date, 'dd.MM.yyyy') : defaultOutput;

// Source from https://github.com/date-fns/date-fns/issues/1218#issuecomment-599182307
const FORMAT_RELATIVE_DATE_ONLY_LOCALE_TOKEN = {
  lastWeek: "'Last week' (dd.MM)",
  yesterday: "'Yesterday' (dd.MM)",
  today: "'Today'",
  tomorrow: "'Tomorrow' (dd.MM)",
  nextWeek: "'Next week' (dd.MM)",
  other: 'dd.MM.yyyy',
};

const FORMAT_RELATIVE_DATE_ONLY_LOCALE: Locale = {
  ...localeEnGB,
  formatRelative: (token: string) => {
    switch (token) {
      case 'lastWeek':
      case 'yesterday':
      case 'today':
      case 'tomorrow':
      case 'nextWeek':
      case 'other':
        return FORMAT_RELATIVE_DATE_ONLY_LOCALE_TOKEN[token];
    }

    return 'dd.MM.yyyy';
  },
};

export const formatRelativeDateOnly = (date: Date, baseDate: Date) => {
  return formatRelative(date, baseDate, { locale: FORMAT_RELATIVE_DATE_ONLY_LOCALE });
};

export const getTimeInHoursDisplay = (timeInHours: number) => {
  if (timeInHours === 0) return '0';
  if (!Number.isFinite(timeInHours)) return '';

  const sign = timeInHours < 0 ? '-' : '';
  const absHours = Math.abs(timeInHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  const result = [];

  if (hours > 0) {
    result.push(`${hours}h`);
  }

  if (minutes > 0) {
    result.push(`${minutes}m`);
  }

  return sign + result.join(' ');
};

export const createTimeInHoursFormatter =
  ({ valueWhenUndefined: valueWhenTimeUndefined }: { valueWhenUndefined: string | undefined }) =>
  (timeInHours: number | undefined) => {
    if (timeInHours === undefined) {
      return valueWhenTimeUndefined;
    }
    return getTimeInHoursDisplay(timeInHours);
  };

export const formatDurationWithSeconds = (durationInHours: number) => {
  if (durationInHours === 0) return '0';
  if (!Number.isFinite(durationInHours)) return '';

  const sign = durationInHours < 0 ? '-' : '';
  const totalSeconds = Math.abs(durationInHours) * 3600;
  let remainingSeconds = totalSeconds;

  const result = [];

  const hours = Math.floor(remainingSeconds / 3600);
  if (hours > 0) {
    result.push(`${hours}h`);
    remainingSeconds -= hours * 3600;
  }

  const minutes = Math.floor(remainingSeconds / 60);
  if (minutes > 0) {
    result.push(`${minutes}m`);
    remainingSeconds -= minutes * 60;
  }

  if (remainingSeconds > 0) {
    const rounded = remainingSeconds.toFixed(0);
    const removedZeros = Number.parseFloat(rounded);

    if (removedZeros > 0) {
      result.push(`${removedZeros}s`);
    }
  }

  return sign + result.join(' ');
};

export const convertLocalToUTC = (localDate: Date) => {
  const offset = localDate.getTimezoneOffset();
  const result = addMinutes(localDate, -offset);

  return result;
};

export type DateRange = { from: Date | undefined; to?: Date };

export const isWithinDateRangeInDays = ({
  days,
  rangeValue,
}: {
  days: number;
  rangeValue: DateRange;
}) => {
  if (!rangeValue?.from || !rangeValue?.to) {
    return false;
  }
  return differenceInDays(rangeValue.to, rangeValue.from) < days;
};

export const setCustomDateRangeToFullDay = (
  onSelectedChange?: (selectedChange: SelectedDateRange) => void,
) => {
  if (!onSelectedChange) {
    return;
  }

  return (selectedDateRange: SelectedDateRange) =>
    onSelectedChange(
      selectedDateRange.value === 'custom'
        ? {
            value: selectedDateRange.value,
            from: selectedDateRange.from && startOfDay(selectedDateRange.from),
            to: selectedDateRange.to && endOfDay(selectedDateRange.to),
          }
        : selectedDateRange,
    );
};

export const isDateValid = (value: string) => {
  const parsedDate = new Date(value);

  const result = isValid(parsedDate);

  return result;
};

export const isDateInThePast = (value: string) => {
  const yesterday = subDays(startOfDay(new Date()), 1);
  const inputDate = parse(value, 'yyyy-MM-dd', new Date());
  return isAfter(inputDate, yesterday);
};

export const isTimeRangeForToday = (from: Date, to: Date) => {
  return isToday(from) && isToday(to);
};

export const getPartOfDay = (date: Date) => {
  const now = getHours(date);

  if (now >= 0 && now < 12) {
    return 'morning';
  }
  if (now >= 12 && now < 18) {
    return 'afternoon';
  }
  if (now >= 18 && now <= 23) {
    return 'evening';
  }

  throw new Error(`Failed to get hour of the date: ${date}`);
};

// https://github.com/unicode-org/cldr-json/blob/46.1.0/cldr-json/cldr-core/supplemental/weekData.json#L59
export const getWeekStartsOn = (countryCode?: CountryCodeAlpha3): Day => {
  switch (countryCode) {
    case 'ALB':
    case 'AUT':
    case 'AZE':
    case 'HRV':
    case 'CYP':
    case 'CZE':
    case 'DNK':
    case 'EST':
    case 'FIN':
    case 'GEO':
    case 'DEU':
    case 'GRC':
    case 'HUN':
    case 'ISL':
      return MONDAY;
    case 'ISR':
    case 'JPN':
      return SUNDAY;
    case 'KAZ':
    case 'XKX':
    case 'LVA':
    case 'LTU':
    case 'LUX':
    case 'MLT':
    case 'MKD':
    case 'NOR':
    case 'POL':
    case 'ROU':
    case 'SRB':
    case 'SVK':
    case 'SVN':
    case 'SWE':
    case 'UZB':
      return MONDAY;
  }
  return MONDAY;
};

export const getDateTimeInUTC = ({
  date,
  format = "yyyy-MM-dd'T'HH:mm:ssXXX",
}: {
  date: TZDate | string | Date;
  format?: string;
}) => {
  return formatInTimeZone(date, 'UTC', format);
};

export const generateTimeSlots = ({
  date,
  step,
  timezone,
}: {
  date?: string;
  step?: number;
  timezone?: Timezone;
} = {}) => {
  if (!date) {
    return [];
  }

  const start = startOfDay(fromZonedTime(date, timezone?.toString() ?? ''), {
    in: timezone && tz(timezone),
  });
  const end = endOfDay(start, { in: timezone && tz(timezone) });

  const slots = eachMinuteOfInterval(
    {
      start,
      end,
    },
    {
      step,
      in: timezone && tz(timezone),
    },
  );

  return slots;
};

/** Formats a Date as a local ISO 8601 datetime string without timezone offset (e.g. "2024-01-01T00:00:00"). */
export const formatLocalISO = (date: Date): string => format(date, "yyyy-MM-dd'T'HH:mm:ss");

export const getTimeInMinutesDisplay = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) {
    return `${remainingMinutes}min`;
  }

  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}min`;
};
